import { ActivatedRoute } from '@angular/router';
import dayjs from 'dayjs';

export const calculateUnits = (
  startTime: string,
  endTime: string,
  breakStart?: string,
  breakEnd?: string
) => {
  if (!startTime || !endTime) return '';

  let start = new Date(
    `1970-01-01T${
      dayjs(startTime).isValid()
        ? dayjs(startTime).format('HH:mm:ss')
        : startTime
    }`
  );
  let end = new Date(
    `1970-01-01T${
      dayjs(endTime).isValid() ? dayjs(endTime).format('HH:mm:ss') : endTime
    }`
  );
  let breakStartTime = breakStart ? new Date(`1970-01-01T${breakStart}`) : null;
  let breakEndTime = breakEnd ? new Date(`1970-01-01T${breakEnd}`) : null;

  // Handle night shift (if end time is before or equal to start time, add 24h to end time)
  if (end <= start) {
    end.setDate(end.getDate() + 1);
  }

  // Handle break time crossing midnight
  if (breakStartTime && breakStartTime < start) {
    breakStartTime.setDate(breakStartTime.getDate() + 1);
  }
  if (breakEndTime && breakStartTime && breakEndTime <= breakStartTime) {
    breakEndTime.setDate(breakEndTime.getDate() + 1);
  }

  // Calculate total work duration (in milliseconds)
  let workDuration = end.getTime() - start.getTime();

  // Subtract break duration if valid
  if (
    breakStartTime &&
    breakEndTime &&
    breakStartTime >= start &&
    breakEndTime <= end
  ) {
    workDuration -= breakEndTime.getTime() - breakStartTime.getTime();
  }

  const hours = Math.floor(workDuration / (1000 * 60 * 60));
  const minutes = Math.floor((workDuration % (1000 * 60 * 60)) / (1000 * 60));

  return isNaN(hours) || isNaN(minutes) ? '' : `${hours}h ${minutes}m`;
};

export const isNightShift = (startTime: string, endTime: string): boolean => {
  const start = new Date(
    `1970-01-01T${
      dayjs(startTime).isValid()
        ? dayjs(startTime).format('HH:mm:ss')
        : startTime
    }`
  );
  const end = new Date(
    `1970-01-01T${
      dayjs(endTime).isValid() ? dayjs(endTime).format('HH:mm:ss') : endTime
    }`
  );

  return end < start;
};

export function getAllRouteParams(route: ActivatedRoute): {
  [key: string]: any;
} {
  const allParams: { [key: string]: any } = {};
  let current: ActivatedRoute | null = route.root;

  while (current) {
    Object.assign(allParams, current.snapshot.params);
    current = current.firstChild!;
  }

  return allParams;
}

export const uploadFilenameValidators = (fileName: any) => {
  // Check if filename contains "+" or "%" and exceeds 100 characters
  const hasSpecialChars = /[+%]/.test(fileName);
  const isTooLong = fileName.length > 100;

  if (hasSpecialChars) {
    return 'The filename must not contain any of these characters: +%';
  }

  if (isTooLong) {
    return 'The filename must not exceed 100 characters.';
  }
  return false;
};

export const base64ToFile = (
  base64: string,
  filename: string,
  mimeType: string
) => {
  const arr = base64.split(',');
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mimeType });
};

export function checkIfCameraExists(): Promise<boolean> {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    console.warn('MediaDevices API not supported.');
    return Promise.resolve(false);
  }

  return navigator.mediaDevices
    .enumerateDevices()
    .then((devices) => {
      const hasCamera = devices.some((device) => device.kind === 'videoinput');
      return hasCamera;
    })
    .catch((error) => {
      console.error('Error checking for camera:', error);
      return false;
    });
}
